import React, { useEffect, useState } from 'react';
import { PageHero } from '../components/PageHelpers';
import { Sec } from '../components/Section';
import { Btn, Crd } from '../components/Button';
import { ScoringWidget } from '../widgets';
import { MockupGraphic } from '../components/MockupGraphic';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '../components/BrandIcons';
import { supabase } from '../lib/supabase';
import { emailService } from '../services/emailService';

export const ScorePage = () => {

  const { theme } = useTheme();

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [errors, setErrors] = useState({});
  const [otpSuccess, setOtpSuccess] = useState('');

  const [formData, setFormData] = useState({
    deckFile: null,
    name: '',
    email: '',
    mobile_number: ''
  });

  // Handle resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
      setOtpSuccess('');
      setOtpError('');
    }

    // Reset email verification if email changes
    if (name === 'email' && (emailVerified || otpSent)) {
      setEmailVerified(false);
      setOtpSent(false);
      setOtp('');
      setOtpError('');
    }
  };


  const handleSendOTP = async () => {
    if (!formData.email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }

    setVerifyingEmail(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      // Step 1: Check if email already exists in Supabase
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', formData.email)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Email already exists
        setErrors(prev => ({
          ...prev,
          email: 'Email already registered. Please login.'
        }));
        setVerifyingEmail(false);
        return;
      }

      // Step 2: Send OTP if email does NOT exist
      const result = await emailService.sendOTP(formData.email, formData.name);

      if (result.success) {
        setOtpSent(true);
        startResendTimer();
        setErrors(prev => ({ ...prev, email: '' }));
        setOtpSuccess(`Verification code sent to ${formData.email}`);
      } else {
        setOtpError(result.error || 'Failed to send verification code.');
      }

    } catch (error) {
      console.error(error);
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setOtpError('Please enter the verification code');
      return;
    }

    setVerifyingEmail(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      const result = emailService.verifyOTP(formData.email, otp);

      if (result.success) {
        setEmailVerified(true);
        setOtpSent(false);
        setOtp('');
        setErrors(prev => ({ ...prev, email: '' }));
        setOtpSuccess('Email verified successfully');
      } else {
        setOtpError(result.error || 'Invalid verification code.');
      }
    } catch (error) {
      console.error(error);
      setOtpError('Failed to verify code.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setVerifyingEmail(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      const result = await emailService.sendOTP(formData.email, formData.name);

      if (result.success) {
        startResendTimer();
        setOtpSuccess('New verification code sent!');
      } else {
        setOtpError(result.error || 'Failed to resend code.');
      }
    } catch (error) {
      console.error(error);
      setOtpError('Failed to resend code.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  // Optional: Clear OTP when email changes
  useEffect(() => {
    if (formData.email && (emailVerified || otpSent)) {
      // Clear OTP when email changes
      emailService.clearOTP(formData.email);
      setEmailVerified(false);
      setOtpSent(false);
      setOtp('');
      setOtpError('');
    }
  }, [formData.email]);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Mobile number validation
    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = 'Mobile number is required';
    } else if (!/^\+?[\d\s-]{10,15}$/.test(formData.mobile_number.trim())) {
      newErrors.mobile_number = 'Please enter a valid mobile number (10-15 digits)';
    }

    // File validation
    if (!formData.deckFile) {
      newErrors.deckFile = 'Please upload your deck';
    } else {
      const fileExtension = formData.deckFile.name.split('.').pop().toLowerCase();
      if (!['pdf', 'pptx'].includes(fileExtension)) {
        newErrors.deckFile = 'Only PDF and PPTX files are supported';
      }
      // Check file size (max 50MB)
      if (formData.deckFile.size > 50 * 1024 * 1024) {
        newErrors.deckFile = 'File size must be less than 50MB';
      }
    }

    // Email verification check
    if (!emailVerified) {
      newErrors.email = 'Please verify your email before submitting';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const file = formData.deckFile;
      const fileName = `${Date.now()}_${file.name}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase
        .storage
        .from("free_decks_analysis")
        .upload(fileName, file);

      if (uploadError) {
        console.error(uploadError);
        setErrors({ submit: "File upload failed. Please try again." });
        setLoading(false);
        return;
      }

      // Get file public URL
      const { data } = supabase
        .storage
        .from("free_decks_analysis")
        .getPublicUrl(fileName);

      const fileUrl = data.publicUrl;

      // Insert submission into table
      const { error: insertError } = await supabase
        .from("free_deck_analysis")
        .insert([
          {
            name: formData.name,
            email: formData.email,
            mobile_number: formData.mobile_number,
            deck_file_url: fileUrl
          }
        ]);

      if (insertError) {
        console.error(insertError);
        setErrors({ submit: "Failed to save submission. Please try again." });
        setLoading(false);
        return;
      }

      setSubmitted(true);

    } catch (error) {
      console.error(error);
      setErrors({ submit: "Something went wrong. Please try again." });
    }

    setLoading(false);
  };

  return (
    <div data-testid="score-page">
      <PageHero
        eyebrow="FREE DECK ANALYSIS"
        title="Get Your Free"
        accent="DecX Score"
        subtitle="Upload your deck. Get scored across 5 dimensions in 24 hours."
        body="No credit card required. 100% confidential."
      >
        <MockupGraphic type="score" label="DecX Score Dashboard" />
      </PageHero>

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {!submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Crd glow>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  Upload Your Deck
                </h2>
                <p style={{
                  fontSize: '14px',
                  opacity: 0.6,
                  textAlign: 'center',
                  marginBottom: '32px'
                }}>
                  PDF or PPTX supported
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Name */}
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      style={{
                        width: '100%',
                        padding: '14px',
                        borderRadius: '12px',
                        border: `1px solid ${errors.name 
                          ? '#ef4444' 
                          : theme === 'dark'
                            ? 'rgba(255,255,255,0.2)'
                            : 'rgba(0,0,0,0.2)'}`,
                        background: theme === 'dark'
                          ? 'rgba(255,255,255,0.05)'
                          : 'rgba(0,0,0,0.02)',
                        color: theme === 'dark' ? C.white : C.black
                      }}
                    />
                    {errors.name && (
                      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginLeft: '4px' }}>
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Email with OTP Verification */}
                  <div>
                    {!otpSent && !emailVerified ? (
                      // Send OTP button view
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Work Email"
                          style={{
                            flex: 1,
                            padding: '14px',
                            borderRadius: '12px',
                            border: `1px solid ${errors.email 
                              ? '#ef4444' 
                              : theme === 'dark'
                                ? 'rgba(255,255,255,0.2)'
                                : 'rgba(0,0,0,0.2)'}`,
                            background: theme === 'dark'
                              ? 'rgba(255,255,255,0.05)'
                              : 'rgba(0,0,0,0.02)',
                            color: theme === 'dark' ? C.white : C.black
                          }}
                        />
                        <Btn
                          type="button"
                          onClick={handleSendOTP}
                          disabled={verifyingEmail}
                          style={{
                            padding: '14px 20px',
                            borderRadius: '12px',
                            border: 'none',
                            background: verifyingEmail ? 'rgba(59, 130, 246, 0.5)' : '#3b82f6',
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: verifyingEmail ? 'not-allowed' : 'pointer',
                            opacity: verifyingEmail ? 0.7 : 1,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {verifyingEmail ? 'Sending...' : 'Send Code'}
                        </Btn>
                      </div>
                    ) : !emailVerified ? (
                      // Enter OTP view
                      <div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            placeholder="Enter 6-digit code"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            style={{
                              flex: 1,
                              padding: '14px',
                              borderRadius: '12px',
                              border: `1px solid ${otpError ? '#ef4444' : 'rgba(0,0,0,0.2)'}`,
                              background: theme === 'dark'
                                ? 'rgba(255,255,255,0.05)'
                                : 'rgba(0,0,0,0.02)',
                              color: theme === 'dark' ? C.white : C.black,
                              textAlign: 'center',
                              fontSize: '18px',
                              letterSpacing: '4px'
                            }}
                            maxLength={6}
                          />
                          <Btn
                            type="button"
                            onClick={handleVerifyOTP}
                            disabled={verifyingEmail || !otp}
                            style={{
                              padding: '14px 20px',
                              borderRadius: '12px',
                              border: 'none',
                              background: (!otp || verifyingEmail) ? 'rgba(59, 130, 246, 0.5)' : '#3b82f6',
                              color: '#ffffff',
                              fontWeight: 600,
                              fontSize: '14px',
                              cursor: (!otp || verifyingEmail) ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {verifyingEmail ? 'Verifying...' : 'Verify'}
                          </Btn>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={handleResendOTP}
                            disabled={resendTimer > 0}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: resendTimer > 0 ? '#94a3b8' : '#3b82f6',
                              fontSize: '12px',
                              cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                              textDecoration: 'underline'
                            }}
                          >
                            {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setOtpSent(false);
                              setOtp('');
                              setOtpError('');
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              fontSize: '12px',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                          >
                            Use different email
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Verified view
                      <div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: '#10b98110',
                          borderRadius: '12px',
                          border: '1px solid #10b981'
                        }}>
                          <input
                            type="email"
                            value={formData.email}
                            disabled
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'transparent',
                              color: theme === 'dark' ? C.white : C.black,
                              opacity: 0.7
                            }}
                          />
                          <CheckCircleIcon size={20} color="#10b981" />
                        </div>
                      </div>
                    )}
                    {errors.email && (
                      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginLeft: '4px' }}>
                        {errors.email}
                      </p>
                    )}
                    {otpError && (
                      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginLeft: '4px' }}>
                        {otpError}
                      </p>
                    )}
                    {otpSuccess && (
                      <p style={{ 
                        color: '#10b981', 
                        fontSize: '12px', 
                        marginTop: '4px', 
                        marginLeft: '4px' 
                      }}>
                        {otpSuccess}
                      </p>
                    )}
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <input
                      type="tel"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      placeholder="Mobile Number (e.g., +1 234 567 8900)"
                      style={{
                        width: '100%',
                        padding: '14px',
                        borderRadius: '12px',
                        border: `1px solid ${errors.mobile_number 
                          ? '#ef4444' 
                          : theme === 'dark'
                            ? 'rgba(255,255,255,0.2)'
                            : 'rgba(0,0,0,0.2)'}`,
                        background: theme === 'dark'
                          ? 'rgba(255,255,255,0.05)'
                          : 'rgba(0,0,0,0.02)',
                        color: theme === 'dark' ? C.white : C.black
                      }}
                    />
                    {errors.mobile_number && (
                      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginLeft: '4px' }}>
                        {errors.mobile_number}
                      </p>
                    )}
                  </div>

                  {/* File Upload */}
                  <div>
                    <div
                      style={{
                        border: `2px dashed ${errors.deckFile 
                          ? '#ef4444'
                          : theme === 'dark' 
                            ? 'rgba(255,255,255,0.2)' 
                            : 'rgba(0,0,0,0.2)'}`,
                        borderRadius: '16px',
                        padding: '40px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                      }}
                    >
                      <input
                        type="file"
                        name="deckFile"
                        onChange={handleChange}
                        accept=".pdf,.pptx"
                        style={{ display: 'none' }}
                        id="deck-upload"
                        data-testid="deck-file-input"
                      />
                      <label htmlFor="deck-upload" style={{ cursor: 'pointer' }}>
                        <p style={{ fontSize: '16px', opacity: 0.7, marginBottom: '8px' }}>
                          Drop your deck here or click to upload
                        </p>
                        <p style={{ fontSize: '13px', opacity: 0.5 }}>
                          {formData.deckFile ? formData.deckFile.name : 'PDF, PPTX supported'}
                        </p>
                      </label>
                    </div>
                    {errors.deckFile && (
                      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px', marginLeft: '4px' }}>
                        {errors.deckFile}
                      </p>
                    )}
                  </div>

                  {/* Submit Error */}
                  {errors.submit && (
                    <p style={{ 
                      color: '#ef4444', 
                      fontSize: '14px', 
                      textAlign: 'center',
                      padding: '12px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '8px'
                    }}>
                      {errors.submit}
                    </p>
                  )}

                  <Btn
                    type="submit"
                    variant="primary"
                    large
                    disabled={loading || !emailVerified}
                    style={{ 
                      width: '100%',
                      opacity: loading || !emailVerified ? 0.6 : 1,
                      cursor: loading || !emailVerified ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? "Submitting..." : "Get My Free DecX Score"}
                  </Btn>

                </form>
              </Crd>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Crd glow style={{ textAlign: 'center' }}>
                <CheckCircleIcon size={64} gradient style={{ marginBottom: '24px' }} />
                <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '16px' }}>
                  Deck Received!
                </h2>
                <p style={{ fontSize: '18px', opacity: 0.8, marginBottom: '32px' }}>
                  Your DecX Score will be delivered within 24 hours.
                </p>
                <Btn variant="primary" to="/">
                  Back to Home
                </Btn>
              </Crd>
            </motion.div>
          )}
        </div>
      </Sec>
      <ScoringWidget />
    </div>
  );
};